import type { JSX } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { TableSortSelect } from '@/shared/components/TableSortSelect';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useVerificationsForInspection } from '../hooks/useVerificationsForInspection';
import { useApproveVerification } from '../hooks/useApproveVerification';
import { InspectionList } from '../components/InspectionList';
import { ReturnVerificationDialog } from '../components/ReturnVerificationDialog';
import { inspectionTableColumns, INSPECTION_DEFAULT_SORT } from '../config/inspectionTableColumns';
import type { InspectionVerification } from '../types/inspection.types';

/** The inspection statuses a super admin browses, in the order they are shown. */
const TAB_STATUSES = ['ACCEPTED', 'VERIFIED', 'RETURNED'] as const;

type InspectionTabStatus = (typeof TAB_STATUSES)[number];

const TAB_LABELS: Record<InspectionTabStatus, string> = {
  ACCEPTED: 'Awaiting inspection',
  VERIFIED: 'Verified',
  RETURNED: 'Returned',
};

const EMPTY_MESSAGES: Record<InspectionTabStatus, string> = {
  ACCEPTED: 'No verifications awaiting inspection',
  VERIFIED: 'No verified verifications',
  RETURNED: 'No returned verifications',
};

const NO_MATCH_MESSAGE = 'No verifications match the filters';

function emptyMessageFor(status: InspectionTabStatus, activeFilterCount: number): string {
  return activeFilterCount > 0 ? NO_MATCH_MESSAGE : EMPTY_MESSAGES[status];
}

export function VerificationInspectionPage(): JSX.Element {
  const [status, setStatus] = useState<InspectionTabStatus>('ACCEPTED');
  const { verifications, hasData, isLoading, isError, error } =
    useVerificationsForInspection(status);
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    setSort,
    activeFilterCount,
  } = useTableFilters(verifications, inspectionTableColumns, INSPECTION_DEFAULT_SORT);
  const { approveVerification, isLoading: isApproving } = useApproveVerification();
  const [returning, setReturning] = useState<InspectionVerification | null>(null);

  const approve = (verificationId: string): void => {
    approveVerification(verificationId, {
      onSuccess: () => toast.success('Verification approved'),
      onError: () => toast.error('Failed to approve the verification'),
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Verification Inspection</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        <p className="text-muted-foreground">
          Verifications from every company, by the stage of your inspection.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
        <DataTableFilters
          columns={inspectionTableColumns}
          filters={filters}
          activeFilterCount={activeFilterCount}
          onFilterChange={setFilter}
          onClearFilters={clearFilters}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
        <TableSortSelect columns={inspectionTableColumns} sort={sort} onSortChange={setSort} />
      </div>

      {isError && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading verifications: {error?.message ?? 'Unknown error'}
        </div>
      )}

      <Tabs value={status} onValueChange={(value) => setStatus(value as InspectionTabStatus)}>
        <TabsList>
          {TAB_STATUSES.map((tabStatus) => (
            <TabsTrigger key={tabStatus} value={tabStatus}>
              {TAB_LABELS[tabStatus]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={status} className="pt-2">
          <InspectionList
            verifications={filteredSorted}
            isLoading={isLoading}
            isProcessing={isApproving}
            emptyMessage={emptyMessageFor(status, activeFilterCount)}
            onApprove={approve}
            onReturn={setReturning}
          />
        </TabsContent>
      </Tabs>

      <ReturnVerificationDialog verification={returning} onClose={() => setReturning(null)} />
    </div>
  );
}
